import type { InvoiceData, SavedInvoice } from '../types';

const STORAGE_KEYS = {
  CURRENT_DRAFT: 'invoice_agent_draft',
  HISTORY: 'invoice_agent_history',
  THEME: 'invoice_agent_theme',
  SETTINGS: 'invoice_agent_settings',
};

/**
 * Save the current invoice draft to localStorage
 */
export const saveDraft = (data: InvoiceData): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.CURRENT_DRAFT, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save draft', e);
  }
};

/**
 * Load the current draft from localStorage
 */
export const loadDraft = (): InvoiceData | null => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.CURRENT_DRAFT);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

/**
 * Clear the current draft
 */
export const clearDraft = (): void => {
  localStorage.removeItem(STORAGE_KEYS.CURRENT_DRAFT);
};

/**
 * Save an invoice to history
 */
export const saveInvoice = (data: InvoiceData): SavedInvoice[] => {
  try {
    const historyJson = localStorage.getItem(STORAGE_KEYS.HISTORY);
    const history: SavedInvoice[] = historyJson ? JSON.parse(historyJson) : [];

    const newEntry: SavedInvoice = {
      ...data,
      id: data.id || crypto.randomUUID(),
      savedAt: Date.now(),
    };

    // Remove duplicates based on ID if updating
    const filteredHistory = history.filter((h) => h.id !== newEntry.id);
    const updatedHistory = [newEntry, ...filteredHistory];

    localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(updatedHistory));
    return updatedHistory;
  } catch (e) {
    console.error('Failed to save to history', e);
    return [];
  }
};

/**
 * Alias for saveInvoice for backward compatibility
 */
export const saveToHistory = saveInvoice;

/**
 * Load invoice history from localStorage
 */
export const loadHistory = (): SavedInvoice[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.HISTORY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

/**
 * Delete a specific invoice from history
 */
export const deleteInvoice = (id: string): SavedInvoice[] => {
  try {
    const history = loadHistory();
    const updatedHistory = history.filter((h) => h.id !== id);
    localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(updatedHistory));
    return updatedHistory;
  } catch (e) {
    console.error('Failed to delete invoice', e);
    return [];
  }
};

/**
 * Clear all invoice history
 */
export const clearHistory = (): void => {
  localStorage.removeItem(STORAGE_KEYS.HISTORY);
};

/**
 * Get the current theme preference
 */
export const getTheme = (): 'light' | 'dark' => {
  try {
    const theme = localStorage.getItem(STORAGE_KEYS.THEME);
    return theme === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

/**
 * Set the theme preference
 */
export const setTheme = (theme: 'light' | 'dark'): void => {
  localStorage.setItem(STORAGE_KEYS.THEME, theme);
};
